/**
 * Zustand auth store — ports the old Redux `AuthActions` thunks
 * (`init`, `login`, `register`, `loginWith`, `logout`) plus the self-serve
 * account-deletion endpoints.
 *
 * The session (`{ token, user }`) is persisted to localStorage under `yk-auth`
 * via the `persist` middleware so reloads stay logged in until `init()` either
 * refreshes the user (`GET auth/verify`) or logs out on failure.
 *
 * The API client reads the token (and triggers logout on 401/403) through the
 * `configureAuth` handlers wired up at module load below.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { api, ApiError, configureAuth } from '@/lib/api/client';
import { queryClient } from '@/app/providers';
import {
  identify as analyticsIdentify,
  reset as analyticsReset,
  track as analyticsTrack,
  EVENTS,
} from '@/lib/analytics';
import type { User } from '@/types/api';
import {
  signInWithGoogle,
  signInWithFacebook,
  signInWithTwitter,
  firebaseLogout,
  type SocialProvider,
} from '@/lib/firebase';
import {
  readImpersonationMarker,
  clearImpersonationMarker,
} from '@/lib/auth/impersonation';

import type {
  AuthStatus,
  RegisterPayload,
  LoginWithData,
  LoginWithResult,
  DeleteAccountResult,
  CompleteRegistrationPayload,
} from './types';

/** localStorage key used by the `persist` middleware. */
const STORAGE_KEY = 'yk-auth';

/**
 * Impersonation slice — populated when the user arrived at V2 via an admin's
 * `?impersonation_token=` handoff (see `lib/auth/impersonation.ts`). The
 * `active` flag drives the persistent yellow banner; `since` is the local
 * timestamp of when the handoff happened (useful for "Impersonating for 12
 * minutes" UI in future). The marker is sessionStorage-scoped so a brand-new
 * tab on the same admin doesn't inherit the banner — see the rationale in
 * `lib/auth/impersonation.ts`.
 */
export interface ImpersonationState {
  active: boolean;
  since: string | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  status: AuthStatus;
  error: string | null;
  impersonation: ImpersonationState;

  /** On app start: refresh the persisted session via `GET auth/verify`. */
  init: () => Promise<void>;
  /** `POST auth/login` — store `{ accessToken → token, user }`. */
  login: (email: string, password: string) => Promise<User>;
  /** `POST auth/register` — store the new session on success. */
  register: (payload: RegisterPayload) => Promise<User>;
  /**
   * `POST <completeUrl>` (the `auth/completeregistration/...` path produced by a
   * social `complete_profile` result) — finishes a social sign-up by submitting
   * the remaining profile fields, then stores the returned session.
   */
  completeRegistration: (
    completeUrl: string,
    payload: CompleteRegistrationPayload,
  ) => Promise<User>;
  /** Firebase popup → `POST auth/loginwith/:provider/:identifier`. */
  loginWith: (provider: SocialProvider) => Promise<LoginWithResult>;
  /** `POST auth/verify-activation-code` — flips status='I' → 'A' and auto-logs-in. */
  activateAccount: (userId: number, code: string) => Promise<User>;
  /** Best-effort `GET auth/logout`, then clear state + storage + Firebase. */
  logout: () => Promise<void>;
  /** `POST auth/delete-account` — schedule deletion, logs the session out. */
  requestAccountDeletion: (password: string, reason?: string) => Promise<DeleteAccountResult>;
  /** `POST auth/cancel-account-deletion` — revert a pending deletion. */
  cancelAccountDeletion: () => Promise<void>;
  /** Patch the in-memory + persisted user (e.g. after a profile edit). */
  setUser: (user: User) => void;
  /**
   * Shallow-merge `partial` into the cached user (sidebar / header pick up the
   * change immediately). No-op when there's no signed-in user.
   */
  updateUser: (partial: Partial<User>) => void;
  /**
   * Exit an active admin-impersonation session: clear the sessionStorage
   * marker, log out (which revokes the impersonation token server-side and
   * clears the persisted session + every TanStack cache entry), and resolve.
   * The caller is expected to `window.location.href = '/login'` after this
   * resolves — a hard navigation is preferred so any per-target-user module
   * state (e.g. socket listeners, in-flight requests) is torn down.
   */
  exitImpersonation: () => Promise<void>;
}

/** Best-effort extraction of a human-readable message from an unknown error. */
function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}

/** Firebase popup cancellations shouldn't surface as errors. */
function isCancelledPopup(e: unknown): boolean {
  const code = (e as { code?: string } | null)?.code;
  return code === 'auth/cancelled-popup-request' || code === 'auth/popup-closed-by-user';
}

/**
 * The backend includes the social-login record under `user.provider` on a
 * `complete_profile` response; it isn't part of the public `User` type, so read
 * it defensively to build the `completeregistration` URL.
 */
interface SocialProviderRef {
  name?: string;
  identifier?: string;
  id?: number | string;
}
function readProviderRef(user: User): SocialProviderRef {
  return ((user as unknown as { provider?: SocialProviderRef }).provider ?? {}) as SocialProviderRef;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      status: 'idle',
      error: null,
      // Hydrated from sessionStorage at store-creation time so a reload of
      // an in-progress impersonation re-renders the banner without an
      // intermediate flash. The sessionStorage marker is set by
      // `captureImpersonationToken()` during the pre-React boot.
      impersonation: (() => {
        const m = readImpersonationMarker();
        return m
          ? { active: true, since: m.since }
          : { active: false, since: null };
      })(),

      init: async () => {
        const startToken = get().token;
        if (!startToken) {
          set({ status: 'unauthenticated', user: null });
          return;
        }
        set({ status: 'loading', error: null });
        try {
          const user = await api.get<User>('auth/verify');
          // A fresh login() may have swapped the token while verify was
          // in flight. Don't overwrite the live session with the
          // captured (stale) token; just refresh user fields if the
          // token still matches.
          const liveToken = get().token;
          if (liveToken !== startToken) return;
          set({ user, token: startToken, status: 'authenticated', error: null });
          analyticsIdentify(user);
        } catch (e) {
          // Guard against the concurrent-login race (a fresh login()
          // swapped the token while verify was in flight).
          if (get().token !== startToken) return;

          // Only drop the session for a REAL token rejection — HTTP 401
          // with an envelope failure. Network failures (code 0 — WiFi
          // off / server unreachable / CORS / abort) and 5xx errors are
          // transient: keep the persisted user signed in and let the
          // next online request retry. Logging out on every network
          // blip would kick users out of long-lived tabs whenever they
          // lose connectivity. The persisted user comes from localStorage
          // via Zustand's persist middleware; we trust it until the
          // server explicitly tells us the token is bad.
          const isAuthFailure = e instanceof ApiError && e.code === 401;
          if (isAuthFailure) {
            await get().logout();
          } else {
            const persistedUser = get().user;
            set({
              status: persistedUser ? 'authenticated' : 'unauthenticated',
              error: null,
            });
          }
        }
      },

      login: async (email, password) => {
        set({ status: 'loading', error: null });
        try {
          const { accessToken, user } = await api.post<{ accessToken: string; user: User }>(
            'auth/login',
            { email, password },
          );
          set({ token: accessToken, user, status: 'authenticated', error: null });
          analyticsIdentify(user);
          return user;
        } catch (e) {
          set({ status: 'unauthenticated', error: errorMessage(e, 'Unable to sign in') });
          throw e;
        }
      },

      register: async (payload) => {
        set({ status: 'loading', error: null });
        try {
          const { accessToken, user } = await api.post<{ accessToken: string; user: User }>(
            'auth/register',
            payload,
          );
          set({ token: accessToken, user, status: 'authenticated', error: null });
          analyticsIdentify(user);
          return user;
        } catch (e) {
          set({ status: 'unauthenticated', error: errorMessage(e, 'Unable to create account') });
          throw e;
        }
      },

      completeRegistration: async (completeUrl, payload) => {
        set({ status: 'loading', error: null });
        try {
          const { accessToken, user } = await api.post<{ accessToken: string; user: User }>(
            completeUrl,
            payload,
          );
          set({ token: accessToken, user, status: 'authenticated', error: null });
          analyticsIdentify(user);
          return user;
        } catch (e) {
          set({ status: 'unauthenticated', error: errorMessage(e, 'Unable to complete sign-up') });
          throw e;
        }
      },

      // Verify the 6-digit OTP that /auth/request-activation-code emailed.
      // On success the backend flips users.status='I' → 'A' and returns a
      // fully-formed login envelope — same shape as login/register — so we
      // reuse the exact same session-setup pattern here.
      activateAccount: async (userId, code) => {
        set({ status: 'loading', error: null });
        try {
          const { accessToken, user } = await api.post<{ accessToken: string; user: User }>(
            'auth/verify-activation-code',
            { user_id: userId, code },
          );
          set({ token: accessToken, user, status: 'authenticated', error: null });
          analyticsIdentify(user);
          return user;
        } catch (e) {
          set({ status: 'unauthenticated', error: errorMessage(e, 'Unable to activate account') });
          throw e;
        }
      },

      loginWith: async (provider) => {
        set({ status: 'loading', error: null });
        try {
          const profile =
            provider === 'google'
              ? await signInWithGoogle()
              : provider === 'twitter'
                ? await signInWithTwitter()
                : await signInWithFacebook();

          // Guard: some providers (Facebook without App-Review-approved email,
          // Twitter with an unverified email, Apple's "Hide My Email" opt-out,
          // Google when the user picks a no-email workspace account) return
          // a Firebase profile with `email: null`. Without an email we can\'t
          // reliably key the account — abort here with a message the caller
          // surfaces inline, instead of sending an empty-email POST that the
          // backend would reject with a vaguer generic message.
          const email = (profile.email ?? '').trim();
          if (!email) {
            set({
              status: 'unauthenticated',
              error: 'Email address not returned by provider, please use another account',
            });
            throw new Error(
              'Email address not returned by provider, please use another account',
            );
          }

          // The Firebase id token rides along in the body so the backend can
          // verify the popup result server-side.
          const { provider: prov, identifier, ...data } = profile;
          const result = await api.post<LoginWithData>(
            `auth/loginwith/${prov}/${identifier}`,
            data,
          );

          if (result.action === 'login') {
            set({
              token: result.accessToken,
              user: result.user,
              status: 'authenticated',
              error: null,
            });
            analyticsIdentify(result.user);
            return { status: 'authenticated', user: result.user };
          }

          // action === 'account-inactive' — the user's row exists but
          // status='I'. Surface a payload the UI can use to prompt for
          // the email-OTP activation flow. Kept BEFORE the
          // complete_profile branch below because that branch reads
          // `result.user.provider` — the account-inactive payload has
          // no `user` key, so the destructure would blow up on
          // `readProviderRef(result.user)`.
          if (result.action === 'account-inactive') {
            set({ status: 'unauthenticated', error: null });
            return {
              status: 'account_inactive',
              user_id: result.user_id,
              email: result.email,
            };
          }

          // action === 'complete_profile' — the UI must collect the missing
          // fields and POST to the completeregistration endpoint.
          const ref = readProviderRef(result.user);
          const cProvider = ref.name ?? prov;
          const cIdentifier = ref.identifier ?? identifier;
          const cId = ref.id;
          const completeUrl =
            cId != null
              ? `auth/completeregistration/${cProvider}/${cIdentifier}/${cId}`
              : `auth/completeregistration/${cProvider}/${cIdentifier}`;

          set({ status: 'unauthenticated', error: null });
          return {
            status: 'complete_profile',
            provider,
            identifier,
            id: cId,
            fields: result.fields,
            user: result.user,
            hash: result.hash,
            completeUrl,
          };
        } catch (e) {
          if (isCancelledPopup(e)) {
            set({ status: get().token ? 'authenticated' : 'unauthenticated' });
            throw e;
          }
          set({
            status: 'unauthenticated',
            error: errorMessage(e, `Unable to sign in with ${provider}`),
          });
          throw e;
        }
      },

      logout: async () => {
        // Best-effort server-side invalidation; ignore failures.
        try {
          await api.get('auth/logout');
        } catch {
          /* ignore */
        }
        await firebaseLogout();
        set({
          user: null,
          token: null,
          status: 'unauthenticated',
          error: null,
          // Any impersonation context dies with the session — admin will
          // need a fresh handoff. We DON'T clear the sessionStorage marker
          // here because `exitImpersonation()` already handled it; a plain
          // `logout()` from a normal user has marker=absent so this is a
          // no-op anyway.
          impersonation: { active: false, since: null },
        });
        // Drop every cached TanStack query so the next signed-in user can't
        // see the previous user's timeline / counts / notifications / etc.
        // Without this, switching accounts on the same tab leaks per-user
        // data until each query's `staleTime` elapses.
        try {
          queryClient.clear();
        } catch {
          /* ignore */
        }
        // Fire SIGNED_OUT BEFORE resetting Mixpanel identity — once
        // `reset()` runs the distinct_id rotates and the event would be
        // attributed to the anonymous successor.
        try {
          analyticsTrack(EVENTS.SIGNED_OUT);
        } catch {
          /* ignore */
        }
        // Clear Mixpanel identity so the next user's events aren't attributed
        // to the one who just logged out. Mirrors the legacy `AuthActions.js`
        // `analytics.reset()` on logout.
        analyticsReset();
        // Clear the persisted slice so a reload starts clean.
        try {
          useAuth.persist.clearStorage();
        } catch {
          /* ignore */
        }
        // Also clear the legacy CRA-era keys so a user who bounces back to
        // the old REACTJS site after logging out of V2 doesn't find themself
        // still signed in there. See `migrateLegacyAuth.ts` for the inverse.
        try {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('userData');
        } catch {
          /* ignore */
        }
      },

      requestAccountDeletion: async (password, reason) => {
        const result = await api.post<DeleteAccountResult>('auth/delete-account', {
          password,
          ...(reason ? { reason } : {}),
        });
        // Mark the impending logout as intentional so RequireAuth's
        // redirect to /login doesn't surface "Your session expired" —
        // the user just scheduled their own deletion.
        try {
          sessionStorage.setItem('yk-intentional-logout', '1');
        } catch {
          /* ignore */
        }
        // The server invalidates the current token, so log the session out.
        await get().logout();
        return result;
      },

      cancelAccountDeletion: async () => {
        await api.post('auth/cancel-account-deletion');
      },

      setUser: (user) => set({ user }),

      exitImpersonation: async () => {
        // Drop the sessionStorage marker FIRST so a race with a concurrent
        // tab open (or a logout failure) can't leave the banner up over a
        // logged-out chrome. `logout()` itself also resets the slice in
        // memory; the marker clear is what survives across reloads.
        clearImpersonationMarker();
        // Mark this as an intentional logout so RequireAuth's "session
        // expired" banner doesn't fire on the redirect to /login that
        // the caller will trigger.
        try {
          sessionStorage.setItem('yk-intentional-logout', '1');
        } catch {
          /* ignore */
        }
        await get().logout();
      },

      updateUser: (partial) => {
        const current = get().user;
        if (!current) return;
        // Deep-merge the nested `profile` / `settings` / `total` blocks so a
        // partial update (e.g. `{ profile: { about_me: '…' } }`) doesn't blow
        // away the other nested fields.
        const next: User = {
          ...current,
          ...partial,
          profile:
            partial.profile !== undefined
              ? { ...(current.profile ?? {}), ...partial.profile }
              : current.profile,
          settings:
            partial.settings !== undefined
              ? { ...(current.settings ?? {}), ...partial.settings }
              : current.settings,
          total:
            partial.total !== undefined
              ? { ...(current.total ?? {}), ...partial.total }
              : current.total,
        };
        set({ user: next });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Only persist the session itself; status/error are transient.
      partialize: (state) => ({ token: state.token, user: state.user }),
    },
  ),
);

// Wire the API client to this store: it reads the token on every request and
// triggers a logout when the server returns 401/403.
configureAuth({
  getToken: () => useAuth.getState().token,
  onUnauthorized: () => {
    void useAuth.getState().logout();
  },
});
